import { useEffect, useState } from "react";
import { adminListFeedback, adminUpdateFeedback, adminDeleteFeedback } from "@/lib/api";
import { Inbox, Bug, Lightbulb, Check, RotateCcw, Trash2, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => { try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return d; } };

export default function AdminFeedback() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => adminListFeedback().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (it, status) => { try { await adminUpdateFeedback(it.id, status); load(); } catch { toast.error("Could not update"); } };
  const remove = async (it) => { if (!window.confirm("Delete this feedback?")) return; try { await adminDeleteFeedback(it.id); load(); } catch { toast.error("Could not delete"); } };

  const shown = (items || []).filter((x) => filter === "all" || x.status === filter);
  const count = (s) => (items || []).filter((x) => s === "all" || x.status === s).length;

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-feedback">
      <div className="mb-2 flex items-center gap-2.5">
        <Inbox className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Feedback</h1>
      </div>
      <p className="mb-6 text-sm text-ink-3">Bug reports and content suggestions submitted from the public <span className="font-mono text-xs">/feedback</span> page.</p>

      <div className="mb-6 flex flex-wrap gap-2">
        {[["all", "All"], ["new", "New"], ["resolved", "Resolved"]].map(([k, l]) => (
          <button key={k} data-testid={`feedback-filter-${k}`} onClick={() => setFilter(k)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === k ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
            {l} · {count(k)}
          </button>
        ))}
      </div>

      {items === null ? <p className="py-16 text-center text-ink-3">…</p> : shown.length === 0 ? (
        <p className="py-16 text-center text-ink-3">No feedback here yet.</p>
      ) : (
        <ul className="space-y-3">
          {shown.map((it) => {
            const isBug = it.type === "bug";
            return (
              <li key={it.id} data-testid={`feedback-item-${it.id}`} className={`border border-line bg-surface p-4 ${it.status === "resolved" ? "opacity-70" : ""}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${isBug ? "bg-rose-500/15 text-rose-600" : "bg-[var(--gold)]/15 text-gold"}`}>
                    {isBug ? <Bug className="h-3 w-3" /> : <Lightbulb className="h-3 w-3" />} {isBug ? "Bug" : "Suggestion"}
                  </span>
                  {it.area && <span className="rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-2">{it.area}</span>}
                  {it.status === "resolved" && <span className="text-[11px] text-emerald-600">resolved</span>}
                  <span className="ml-auto text-xs text-ink-3">{fmt(it.created_at)}</span>
                </div>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink">{it.message}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-ink-3">
                  {it.email && <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1 hover:text-saffron"><Mail className="h-3 w-3" /> {it.email}</a>}
                  {it.page_url && <a href={it.page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-saffron"><ExternalLink className="h-3 w-3" /> page</a>}
                </div>
                <div className="mt-3 flex gap-2 border-t border-line pt-3">
                  {it.status === "new" ? (
                    <button data-testid={`feedback-resolve-${it.id}`} onClick={() => setStatus(it, "resolved")} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-emerald-500 hover:text-emerald-600"><Check className="h-3.5 w-3.5" /> Mark resolved</button>
                  ) : (
                    <button data-testid={`feedback-reopen-${it.id}`} onClick={() => setStatus(it, "new")} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-saffron hover:text-saffron"><RotateCcw className="h-3.5 w-3.5" /> Reopen</button>
                  )}
                  <button data-testid={`feedback-delete-${it.id}`} onClick={() => remove(it)} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-3 hover:border-[var(--saffron-dark)] hover:text-[var(--saffron-dark)]"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
